import type { ExcelFunctionParameter } from "@/services/custom-functions/customFunction.types";

type ArrayParamGroup = {
  keys: string[];
  indices: number[];
};

function isBlankCellValue(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "");
}

// Flattens a nested array if possible, or throws error if invalid
function flattenNestedArray(objectValue: any[], objectKey: string): any {
  if (Array.isArray(objectValue)) {
    if (objectValue.every((element) => !Array.isArray(element))) {
      // The list is flat
      return objectValue[0];
    } else if (objectValue.every((element) => Array.isArray(element) && element.length === 1)) {
      // The list can be flattened
      return objectValue.map((element) => element[0]);
    } else {
      throw new Error(`The list for ${objectKey} cannot be flattened`);
    }
  } else {
    throw new Error(`Expected an array for ${objectKey} at nest level 1`);
  }
}

// Group parameters by array-object notation like 'arrayName[].prop'
function groupArrayParameters(parameters: ExcelFunctionParameter[]): Record<string, ArrayParamGroup> {
  const arrayParamGroups: Record<string, ArrayParamGroup> = {};

  parameters.forEach((param, idx) => {
    const match = param.name.match(/^(\w+)\[\]\.(.+)$/);
    if (match) {
      const arrName = match[1];
      const propName = match[2];
      if (!arrayParamGroups[arrName]) {
        arrayParamGroups[arrName] = { keys: [], indices: [] };
      }
      arrayParamGroups[arrName].keys.push(propName);
      arrayParamGroups[arrName].indices.push(idx);
    }
  });

  return arrayParamGroups;
}

// Builds array of objects for parameters grouped by array name
function buildArrayParameterObjects(item: any[], group: ArrayParamGroup, arrName: string): any[] {
  const { keys, indices } = group;

  // Verify all arrays have the same length
  const lengths = indices.map((idx) => item[idx].length);
  const expectedLength = lengths[0];
  if (!lengths.every((l) => l === expectedLength)) {
    const details = keys.map((key, i) => `${arrName}[].${key}: ${lengths[i]}`).join(", ");
    throw new Error(`Array parameter group "${arrName}" has inconsistent lengths: ${details}`);
  }

  const arr: any[] = [];

  for (let i = 0; i < expectedLength; i++) {
    const obj: any = {};
    for (let k = 0; k < keys.length; k++) {
      const val = item[indices[k]][i][0];
      if (!isBlankCellValue(val)) {
        obj[keys[k]] = val;
      }
    }

    if (Object.keys(obj).length > 0) {
      arr.push(obj);
    }
  }

  return arr;
}

// Assign nested keys using dot-notation path within an object
function assignNestedKey(obj: Record<string, any>, keyPath: string, value: any): void {
  const keyParts = keyPath.split(".");
  let current = obj;
  keyParts.forEach((part, idx) => {
    if (idx === keyParts.length - 1) {
      current[part] = value;
    } else {
      if (!(part in current)) {
        current[part] = {};
      }
      current = current[part];
    }
  });
}

export function constructExcelRequestItem(item: any[], parameters: ExcelFunctionParameter[]): Record<string, any> {
  const newItem: Record<string, any> = {};
  const arrayParamGroups = groupArrayParameters(parameters);

  // Handle array parameter groups (arrays of objects)
  for (const arrName in arrayParamGroups) {
    const group = arrayParamGroups[arrName];
    const arr = buildArrayParameterObjects(item, group, arrName);
    newItem[arrName] = arr;
  }

  // Handle non-array parameters
  for (let i = 0; i < item.length; i++) {
    const objectKey = parameters[i].name;
    if (objectKey.includes("[].")) {
      // Skip, already handled above
      continue;
    }

    const nestLevel = parameters[i].nestLevel || 0;
    let objectValue = item[i];

    if (nestLevel === 1) {
      objectValue = flattenNestedArray(objectValue, objectKey);
    }

    if (!Array.isArray(objectValue) && isBlankCellValue(objectValue)) {
      continue;
    }

    assignNestedKey(newItem, objectKey, objectValue);
  }

  return newItem;
}
